import React from "react";
import styled from "styled-components";

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: "primary" | "secondary" | "outline" | "ghost" | "error" | "disabled";
    // Старий API для сумісності
    appearance?: "primary" | "secondary" | "outlined" | "ghost" | "error" | "disabled";
    size?: "sm" | "md" | "lg";
    fullWidth?: boolean;
    loading?: boolean;
    children?: React.ReactNode;
}

const ButtonRoot = styled.button<ButtonProps>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: ${({ size }) => {
        if (size === "sm") return "0.5rem 1rem";
        if (size === "lg") return "1rem 2rem";
        return "0.75rem 1.5rem";
    }};
    font-size: ${({ size }) => {
        if (size === "sm") return "0.875rem";
        if (size === "lg") return "1.125rem";
        return "1rem";
    }};
    font-weight: 600;
    border-radius: 0.5rem;
    border: none;
    cursor: ${({ disabled, loading }) => (disabled || loading ? "not-allowed" : "pointer")};
    opacity: ${({ disabled, loading }) => (disabled || loading ? 0.6 : 1)};
    width: ${({ fullWidth }) => (fullWidth ? "100%" : "auto")};
    transition: all 0.2s;

    /* Стиль 1: синя кнопка з білим текстом | Стиль 2: біла кнопка з синім текстом */
    ${({ variant, theme }) => {
        const primary = `
            background-color: ${theme.palette.primary};
            color: ${theme.palette.white};
            border: none;
            &:hover:not(:disabled) {
                background-color: ${theme.palette.primaryHover};
            }
        `;
        const outline = `
            background-color: ${theme.palette.white};
            color: ${theme.palette.primary};
            border: 1px solid ${theme.palette.primary};
            &:hover:not(:disabled) {
                background-color: ${theme.palette.primary};
                color: ${theme.palette.white};
            }
        `;
        const ghost = `
            background-color: transparent;
            color: ${theme.palette.primary};
            border: none;
            &:hover:not(:disabled) {
                background-color: transparent;
                opacity: 0.7;
            }
        `;
        if (variant === "primary" || variant === "error") return primary;
        if (variant === "outline" || variant === "secondary") return outline;
        if (variant === "ghost") return ghost;
        if (variant === "disabled") {
            return `
                background-color: #adb6c2;
                color: #667589;
                cursor: not-allowed;
                opacity: 0.6;
            `;
        }
        return primary;
    }}
`;

export const Button: React.FC<ButtonProps> = ({ children, loading, disabled, appearance, variant, ...props }) => {
    // Мапимо appearance на variant для сумісності
    const mappedVariant = variant || (appearance === "outlined" ? "outline" : appearance);
    
    return (
        <ButtonRoot disabled={disabled || loading} variant={mappedVariant} {...props}>
            {loading ? "Loading..." : children}
        </ButtonRoot>
    );
};

export default Button;
